// Junk readings in the price tape — one marketplace reporting, for a day or two, a price
// no real seat could have (Dave Chappelle, Houston: HelloTickets "$2" on 2026-09-22 while
// Ticket Squeeze had $133 and TicketNetwork $104.65). That one point poisoned the event's
// "lowest ever seen", its 30-day range and a FAQ claiming "+6,700% in 7 days" (ChatGPT
// audit, 2026-10-03). The nightly dent scrub missed it: it only inspects series that have
// a reading today.
//
// A reading is junk when BOTH hold:
//  - it is below JUNK_RATIO × the cheapest TRUSTED reading another marketplace gave for the
//    same event within ±WINDOW days (no real listing sits 80% below every other site), and
//  - it is also an outlier in its own series (below JUNK_RATIO × the series median), or the
//    series is too short to have a median.
// The second condition keeps PERSISTENT disagreements — one partner months apart from the
// others, i.e. a matching problem (Bears at Packers: HelloTickets $69 vs $366 elsewhere) —
// out of this scrub: those need a human look, not a silent deletion.
// With no other marketplace nearby, a reading below JUNK_RATIO × its own series median
// (3+ readings) is junk on its own.
export const JUNK_RATIO = 0.2;
export const WINDOW = 2;

const dayNum = (d) => Date.parse(d + 'T12:00:00Z') / 864e5;
const medianOf = (vals) => {
  const s = [...vals].sort((a, b) => a - b);
  return s.length ? (s.length % 2 ? s[(s.length - 1) / 2] : (s[s.length / 2 - 1] + s[s.length / 2]) / 2) : null;
};

/**
 * @param entry   { source: [[date, price], ...] }
 * @param trusted (source, date) => boolean — readings that may serve as a reference
 * @returns { clean: same shape without junk readings, junk: [{ source, date, price, ref }] }
 */
export function cleanTape(entry, trusted = () => true) {
  const clean = {};
  const junk = [];
  if (!entry || typeof entry !== 'object') return { clean, junk };
  const sources = Object.entries(entry).filter(([, pts]) => Array.isArray(pts));
  // Each marketplace's trusted readings as [day number, price], its dates parsed once: comparing
  // every reading with every other marketplace's parsed both dates per pair (~20,000 Date.parse
  // calls for two months of three marketplaces, ~45 ms of a phone's click to an event page).
  const refs = new Map(sources.map(([s, p]) => [s, p.filter(([d, v]) => v > 0 && trusted(s, d)).map(([d, v]) => [dayNum(d), v])]));
  for (const [source, pts] of sources) {
    const others = sources.filter(([s]) => s !== source).flatMap(([s]) => refs.get(s));
    const own = pts.filter(([, v]) => v > 0).map(([, v]) => v);
    const ownMedian = own.length >= 3 ? medianOf(own) : null;
    clean[source] = pts.filter(([d, v]) => {
      if (!(v > 0)) return false;
      const t = dayNum(d);
      const near = others.filter(([od]) => Math.abs(od - t) <= WINDOW).map(([, ov]) => ov);
      const cross = near.length ? Math.min(...near) : null;
      const ownLow = ownMedian === null || v < JUNK_RATIO * ownMedian;
      const isJunk = cross !== null ? v < JUNK_RATIO * cross && ownLow : ownMedian !== null && v < JUNK_RATIO * ownMedian;
      if (isJunk) junk.push({ source, date: d, price: v, ref: cross ?? ownMedian });
      return !isJunk;
    });
  }
  return { clean, junk };
}
